import type { SourcesResponse } from "@/types/pocketbase-types";
import type { IArticle } from "@/types/extended-types";

// Re-export for convenience
export type { IArticle };

// Convert PocketBase sources to IArticle format
export const sourceToArticle = (source: SourcesResponse): IArticle => {
  return {
    id: source.id,
    slug: source.slug || source.id, // Use slug if available, fallback to id
    title: source.title || "Untitled Article",
    abstract: source.abstract || "No summary available",
    published_date: source.published_date || source.created,
    created: source.created,
    status: source.status || "processed",
    url: source.url || "",
    word_count: source.word_count || 0,
    quiz_taken: (source as any).quiz_taken,
    expand:
      source.expand &&
      typeof source.expand === "object" &&
      "section" in source.expand
        ? {
            section: {
              id: (source.expand as any).section?.id || "",
              name: (source.expand as any).section?.name || "General",
              image: (source.expand as any).section?.image || "",
            },
          }
        : undefined,
  };
};

// Organize articles by date
// Always shows latest 50 articles, grouped by created date
export const organizeArticlesByDate = (articles: IArticle[]) => {
  const dateMap = new Map<string, IArticle[]>();

  // Sort by created date (newest first) and take latest 50
  const sortedArticles = [...articles]
    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
    .slice(0, 50);

  // Create date slots for all unique dates in our articles
  sortedArticles.forEach((article) => {
    const dateKey = new Date(article.created).toISOString().split("T")[0];
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, []);
    }
  });

  // Group articles by date with section deduplication
  sortedArticles.forEach((article) => {
    const articleDate = new Date(article.created).toISOString().split("T")[0];
    const existing = dateMap.get(articleDate) || [];

    // Check if we already have an article from this section for this date
    const sectionId = article.expand?.section?.id;
    const hasSectionArticle = existing.some(
      (existingArticle) => existingArticle.expand?.section?.id === sectionId
    );

    if (!hasSectionArticle && existing.length < 5) {
      // Only add if we don't have an article from this section yet
      dateMap.set(articleDate, [...existing, article]);
    } else if (hasSectionArticle && sectionId) {
      // Replace with newer article from same section if this one is more recent
      const updatedArticles = existing.map((existingArticle) => {
        if (existingArticle.expand?.section?.id === sectionId) {
          // Compare creation times to keep the most recent
          const existingTime = new Date(existingArticle.created).getTime();
          const newTime = new Date(article.created).getTime();
          return newTime > existingTime ? article : existingArticle;
        }
        return existingArticle;
      });
      dateMap.set(articleDate, updatedArticles);
    }
  });

  return dateMap;
};

// Format date for display
export const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const dateKey = date.toISOString().split("T")[0];
  const todayKey = today.toISOString().split("T")[0];
  const yesterdayKey = yesterday.toISOString().split("T")[0];

  if (dateKey === todayKey) {
    return "Today";
  } else if (dateKey === yesterdayKey) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }
};
